"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

interface TermRow {
  id: number;
  name: string;
  gpa: string;
  credits: string;
}

let nextRowId = 0;

function makeRow(): TermRow {
  nextRowId += 1;
  return { id: nextRowId, name: "", gpa: "", credits: "" };
}

interface CumulativeTotals {
  credits: number;
  points: number;
  gpa: number;
  termCount: number;
}

function parseTerm(
  gpa: string,
  credits: string
): { g: number; c: number } | null {
  const g = parseNumberInput(gpa);
  const c = parseNumberInput(credits);
  if (g === null || c === null || g < 0 || c <= 0) {
    return null;
  }
  return { g, c };
}

function computeCumulative(rows: TermRow[]): CumulativeTotals {
  let credits = 0;
  let points = 0;
  let termCount = 0;
  for (const row of rows) {
    const term = parseTerm(row.gpa, row.credits);
    if (term) {
      credits += term.c;
      points += term.g * term.c;
      termCount += 1;
    }
  }
  const gpa = credits > 0 ? points / credits : 0;
  return { credits, points, gpa, termCount };
}

function computeProjection(
  current: CumulativeTotals,
  futureGpa: string,
  futureCredits: string
): { gpa: number; credits: number; points: number } | null {
  const future = parseTerm(futureGpa, futureCredits);
  if (!future || current.credits <= 0) {
    return null;
  }
  const credits = current.credits + future.c;
  const points = current.points + future.g * future.c;
  return { gpa: points / credits, credits, points };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const CUMULATIVE_GPA_CALCULATOR_STRINGS = {
  yourTerms: "Your Terms",
  termNameOptional: "Term name (optional)",
  termPlaceholder: "Term {number}",
  termGpa: "Term GPA",
  credits: "Credits",
  removeTerm: "Remove term {number}",
  remove: "Remove",
  addTerm: "Add Term",
  cumulativeGpa: "Cumulative GPA",
  totalCredits: "Total credits",
  totalQualityPoints: "Total quality points",
  howThisWasCalculated: "How This Was Calculated",
  cumulativeGpaFormulaPrefix:
    "Cumulative GPA = total quality points ÷ total credits = ",
  eachTermContributes:
    "Each term contributes GPA × credits quality points, so heavier terms count for more.",
  projectAFutureTerm: "Project a Future Term",
  addHypotheticalFutureTerm:
    "Add a hypothetical future term to see what your new cumulative GPA would be.",
  futureTermGpa: "Future term GPA",
  futureTermCredits: "Future term credits",
  projectedCumulativeGpa: "Projected cumulative GPA",
  projectedTotalCredits: "Projected total credits",
} as const;

export default function CumulativeGpaCalculatorWidget() {
  const [rows, setRows] = useState<TermRow[]>(() => [makeRow(), makeRow()]);
  const [futureGpa, setFutureGpa] = useState("");
  const [futureCredits, setFutureCredits] = useState("");

  const strings = useToolUi(CUMULATIVE_GPA_CALCULATOR_STRINGS);
  const fmt = useToolNumber();

  const totals = computeCumulative(rows);
  const projection = computeProjection(totals, futureGpa, futureCredits);

  function updateRow(id: number, patch: Partial<TermRow>) {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    setRows((prev) => [...prev, makeRow()]);
  }

  function removeRow(id: number) {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }

  return (
    <>
      {/* Term rows */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.yourTerms}
        </h2>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4"
              key={row.id}
            >
              <div className="min-w-0 basis-full space-y-1 sm:flex-1 sm:basis-auto">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`term-name-${row.id}`}
                >
                  {strings.termNameOptional}
                </label>
                <input
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`term-name-${row.id}`}
                  onChange={(e) => updateRow(row.id, { name: e.target.value })}
                  placeholder={t(strings.termPlaceholder, {
                    number: index + 1,
                  })}
                  type="text"
                  value={row.name}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`term-gpa-${row.id}`}
                >
                  {strings.termGpa}
                </label>
                <input
                  className="w-28 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`term-gpa-${row.id}`}
                  max={5}
                  min={0}
                  onChange={(e) => updateRow(row.id, { gpa: e.target.value })}
                  placeholder="3.50"
                  step="0.01"
                  type="number"
                  value={row.gpa}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`term-credits-${row.id}`}
                >
                  {strings.credits}
                </label>
                <input
                  className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`term-credits-${row.id}`}
                  min={0}
                  onChange={(e) =>
                    updateRow(row.id, { credits: e.target.value })
                  }
                  placeholder="15"
                  step="0.5"
                  type="number"
                  value={row.credits}
                />
              </div>
              <button
                aria-label={t(strings.removeTerm, { number: index + 1 })}
                className="rounded-lg border border-border bg-card px-3 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={rows.length <= 1}
                onClick={() => removeRow(row.id)}
                type="button"
              >
                {strings.remove}
              </button>
            </div>
          ))}
        </div>
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={addRow}
          type="button"
        >
          {strings.addTerm}
        </button>
      </section>

      {/* Results */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard
          label={strings.cumulativeGpa}
          value={fmt(totals.gpa, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        />
        <StatCard label={strings.totalCredits} value={totals.credits} />
        <StatCard
          label={strings.totalQualityPoints}
          value={fmt(totals.points, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}
        />
      </div>

      {/* Working */}
      {totals.termCount > 0 && (
        <section className="space-y-2 rounded-lg border border-border bg-muted/30 p-6">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.howThisWasCalculated}
          </h2>
          <p className="font-mono text-muted-foreground text-sm leading-relaxed">
            {strings.cumulativeGpaFormulaPrefix}
            {fmt(totals.points, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}{" "}
            ÷ {totals.credits} ={" "}
            {fmt(totals.gpa, {
              minimumFractionDigits: 3,
              maximumFractionDigits: 3,
            })}
          </p>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.eachTermContributes}
          </p>
        </section>
      )}

      {/* Projection */}
      <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.projectAFutureTerm}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.addHypotheticalFutureTerm}
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="future-gpa"
            >
              {strings.futureTermGpa}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="future-gpa"
              max={5}
              min={0}
              onChange={(e) => setFutureGpa(e.target.value)}
              placeholder="4.00"
              step="0.01"
              type="number"
              value={futureGpa}
            />
          </div>
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="future-credits"
            >
              {strings.futureTermCredits}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              id="future-credits"
              min={0}
              onChange={(e) => setFutureCredits(e.target.value)}
              placeholder="15"
              step="0.5"
              type="number"
              value={futureCredits}
            />
          </div>
        </div>
        {projection && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <StatCard
                label={strings.projectedCumulativeGpa}
                value={fmt(projection.gpa, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              />
              <StatCard
                label={strings.projectedTotalCredits}
                value={projection.credits}
              />
            </div>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              (
              {fmt(totals.points, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}{" "}
              +{" "}
              {fmt(projection.points - totals.points, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
              ) ÷ ({totals.credits} + {projection.credits - totals.credits}) ={" "}
              {fmt(projection.gpa, {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              })}
            </p>
          </>
        )}
      </section>
    </>
  );
}
